import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { Link } from 'react-router-dom';
import { app } from '../../firebase';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Legend,
} from 'recharts';
import {
  FaUsers, FaUser, FaEye, FaPercentage,
  FaFilter, FaFileAlt, FaGlobe, FaMobileAlt, FaSyncAlt,
  FaLink, FaChartLine, FaSearch, FaInfoCircle,
} from 'react-icons/fa';
import SeoReport from './SeoReport';

const functions = getFunctions(app);
const getGa4Data   = httpsCallable(functions, 'getGa4Data');
const getMozDataFn = httpsCallable(functions, 'getMozData');
const getGscDataFn = httpsCallable(functions, 'getGscData');

const DATE_RANGES = [
  { label: '7 days',  value: 7  },
  { label: '28 days', value: 28 },
  { label: '90 days', value: 90 },
];

const COMPETITOR_ORDER = [
  'hiremeja.com',
  'caribbeanjobs.com',
  'jobsinja.com',
  'hirelatam.com',
];

// ─── Helpers ──────────────────────────────────────────────────

const fmt = (n, decimals = 0) =>
  n == null ? '—' : Number(n).toLocaleString('en-US', {
    maximumFractionDigits: decimals,
  });

const pct = (n) =>
  n == null ? '—' : `${(Number(n) * 100).toFixed(1)}%`;

const fmtDuration = (seconds) => {
  if (!seconds) return '—';
  const m = Math.floor(seconds / 60);
  const s = Math.round(seconds % 60);
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
};

const dim = (row, i = 0) =>
  row?.dimensionValues?.[i]?.value ?? '';

const met = (row, i = 0) =>
  Number(row?.metricValues?.[i]?.value ?? 0);

const formatGa4Date = (dateStr) => {
  if (!dateStr || dateStr.length !== 8) return dateStr;
  const d = new Date(
    `${dateStr.slice(0, 4)}-${dateStr.slice(4, 6)}-${dateStr.slice(6, 8)}`
  );
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

// ─── Sub-components ───────────────────────────────────────────

const InfoTooltip = ({ text }) => {
  const [visible, setVisible] = useState(false);
  const [coords, setCoords]   = useState({ x: 0, y: 0 });
  const ref = useRef(null);

  const show = (e) => {
    const rect = ref.current.getBoundingClientRect();
    setCoords({ x: rect.left + rect.width / 2, y: rect.top });
    setVisible(true);
  };

  return (
    <>
      <span
        ref={ref}
        className="inline-flex items-center ml-1.5 cursor-help align-middle"
        onMouseEnter={show}
        onMouseLeave={() => setVisible(false)}
      >
        <FaInfoCircle size={11} className="text-gray-300 hover:text-gray-400 transition-colors" />
      </span>
      {visible && createPortal(
        <div
          className="fixed z-[9999] w-56 bg-gray-900 text-white text-xs rounded-lg px-3 py-2 leading-relaxed shadow-lg pointer-events-none normal-case tracking-normal font-normal"
          style={{ left: coords.x, top: coords.y - 8, transform: 'translate(-50%, -100%)' }}
        >
          {text}
          <span className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-900" />
        </div>,
        document.body
      )}
    </>
  );
};

const StatCard = ({ icon, label, value, sub, color = 'text-blue-950', bgColor = 'bg-blue-50', tooltip }) => (
  <div className="bg-white rounded-lg border border-gray-200 p-5 flex items-start gap-4">
    <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${color} ${bgColor}`}>
      {icon}
    </div>
    <div>
      <p className="text-xs text-gray-500 font-medium uppercase tracking-wide flex items-center">
        {label}
        {tooltip && <InfoTooltip text={tooltip} />}
      </p>
      <p className="text-2xl font-bold text-blue-950 mt-0.5">{value}</p>
      {sub && <p className="text-xs text-gray-400 mt-0.5">{sub}</p>}
    </div>
  </div>
);

const SectionHeader = ({ icon, title }) => (
  <div className="flex items-center gap-2 mb-4">
    <span className="text-blue-950">{icon}</span>
    <h3 className="text-base font-bold text-blue-950">{title}</h3>
  </div>
);

const LoadingSpinner = () => (
  <div className="flex justify-center items-center py-16">
    <div className="w-8 h-8 border-4 border-blue-950 border-t-transparent rounded-full animate-spin" />
  </div>
);

const ErrorBox = ({ message, onRetry }) => (
  <div className="bg-red-50 border border-red-200 rounded-lg p-5 text-center">
    <p className="text-red-700 text-sm font-medium">{message}</p>
    {onRetry && (
      <button
        onClick={onRetry}
        className="mt-3 text-sm text-blue-950 font-semibold hover:text-[#cddd3a] flex items-center gap-1 mx-auto"
      >
        <FaSyncAlt size={12} /> Try again
      </button>
    )}
  </div>
);

const CHANNEL_COLORS = {
  'Organic Search': '#1e3a5f',
  'Direct':         '#cddd3a',
  'Referral':       '#6366f1',
  'Organic Social': '#ec4899',
  'Paid Search':    '#f97316',
  'Email':          '#14b8a6',
  'Unassigned':     '#9ca3af',
};
const channelColor = (name) => CHANNEL_COLORS[name] || '#9ca3af';

const PositionBadge = ({ pos }) => {
  const p = Number(pos);
  if (!p) return <span className="text-gray-400">—</span>;
  const color = p <= 3
    ? 'bg-green-100 text-green-700'
    : p <= 10
    ? 'bg-yellow-100 text-yellow-700'
    : 'bg-gray-100 text-gray-500';
  return (
    <span className={`text-xs font-semibold px-1.5 py-0.5 rounded ${color}`}>
      #{p.toFixed(1)}
    </span>
  );
};

// ─── Main component ───────────────────────────────────────────

const MarketingDashboard = () => {
  const [days, setDays]             = useState(28);
  const [overview, setOverview]     = useState(null);
  const [timeseries, setTimeseries] = useState(null);
  const [channels, setChannels]     = useState(null);
  const [pages, setPages]           = useState(null);
  const [countries, setCountries]   = useState(null);
  const [devices, setDevices]       = useState(null);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState(null);
  const [moz, setMoz]               = useState(null);
  const [mozLoading, setMozLoading] = useState(true);
  const [mozError, setMozError]     = useState(null);
  const [gscQueries, setGscQueries] = useState(null);
  const [gscLoading, setGscLoading] = useState(true);
  const [gscError, setGscError]     = useState(null);
  const [showReport, setShowReport] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    setMozLoading(true);
    setMozError(null);
    setGscLoading(true);
    setGscError(null);

    // GA4 + GSC in parallel, independent error handling
    const [ga4Result, gscResult] = await Promise.allSettled([
      Promise.all([
        getGa4Data({ type: 'overview',   days }),
        getGa4Data({ type: 'timeseries', days }),
        getGa4Data({ type: 'channels',   days }),
        getGa4Data({ type: 'pages',      days }),
        getGa4Data({ type: 'countries',  days }),
        getGa4Data({ type: 'devices',    days }),
      ]),
      getGscDataFn({ type: 'queries', days }),
    ]);

    if (ga4Result.status === 'fulfilled') {
      const [ovRes, tsRes, chRes, pRes, cRes, dRes] = ga4Result.value;
      setOverview(ovRes.data);
      setTimeseries(tsRes.data?.rows || []);
      setChannels(chRes.data?.rows || []);
      setPages(pRes.data?.rows || []);
      setCountries(cRes.data?.rows || []);
      setDevices(dRes.data?.rows || []);
    } else {
      console.error('GA4 fetch error:', ga4Result.reason);
      setError(ga4Result.reason?.message || 'Failed to load Analytics data.');
    }
    setLoading(false);

    if (gscResult.status === 'fulfilled') {
      setGscQueries(gscResult.value.data?.rows || []);
    } else {
      console.error('GSC fetch error:', gscResult.reason);
      setGscError(
        gscResult.reason?.message || 'Failed to load Search Console data.'
      );
    }
    setGscLoading(false);

    // Moz separately — slower due to external API
    try {
      const mozRes = await getMozDataFn();
      setMoz(mozRes.data || {});
    } catch (err) {
      console.error('Moz fetch error:', err);
      setMozError(err.message || 'Failed to load Moz data.');
    } finally {
      setMozLoading(false);
    }
  }, [days]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const ovRow          = overview?.rows?.[0] ?? {};
  const totalSessions  = met(ovRow, 0);
  const totalUsers     = met(ovRow, 1);
  const totalPageviews = met(ovRow, 2);
  const engagementRate = met(ovRow, 3);
  const avgDuration    = met(ovRow, 4);
  const newUsers       = met(ovRow, 5);

  const organicRow      = (channels || []).find(r => dim(r, 0) === 'Organic Search');
  const organicSessions = organicRow ? met(organicRow, 0) : null;

  const chartData = (timeseries || []).map((r) => ({
    date:     dim(r, 0),
    Sessions: met(r, 0),
    Users:    met(r, 1),
  }));

  // Sort competitors by DA descending (sites is an array)
  const mozSites = moz?.sites
    ? [...moz.sites].sort(
        (a, b) => (b.domain_authority || 0) - (a.domain_authority || 0)
      )
    : [];

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-blue-950">Marketing Dashboard</h1>
          <p className="text-sm text-gray-500 mt-1">
            Google Analytics 4 · Search Console · Moz
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {DATE_RANGES.map((r) => (
            <button
              key={r.value}
              onClick={() => setDays(r.value)}
              className={`px-4 py-2 rounded-md text-sm font-semibold transition-colors ${
                days === r.value
                  ? 'bg-blue-950 text-white'
                  : 'bg-white border border-gray-200 text-gray-600 hover:border-blue-950 hover:text-blue-950'
              }`}
            >
              {r.label}
            </button>
          ))}
          <Link
            to="/admin/backlink-tracker"
            className="flex items-center gap-2 px-4 py-2 rounded-md bg-white border border-gray-200 text-sm font-semibold text-gray-600 hover:border-blue-950 hover:text-blue-950 transition-colors whitespace-nowrap"
          >
            <FaLink size={12} /> Backlink Tracker
          </Link>
          <button
            onClick={() => setShowReport(true)}
            className="flex items-center gap-2 px-4 py-2 rounded-md text-sm font-semibold transition-colors whitespace-nowrap bg-blue-950 text-white hover:bg-blue-900"
          >
            <FaFileAlt size={12} /> SEO Report
          </button>
          <button
            onClick={fetchAll}
            disabled={loading}
            className="p-2 rounded-md bg-white border border-gray-200 text-gray-500 hover:text-blue-950 hover:border-blue-950 transition-colors disabled:opacity-40"
            title="Refresh"
          >
            <FaSyncAlt size={14} className={loading ? 'animate-spin' : ''} />
          </button>
        </div>
      </div>

      {showReport && (
        <SeoReport
          days={days}
          overview={overview}
          channels={channels}
          pages={pages}
          countries={countries}
          devices={devices}
          timeseries={timeseries}
          gscQueries={gscQueries}
          moz={moz}
          onClose={() => setShowReport(false)}
        />
      )}

      {error && <ErrorBox message={error} onRetry={fetchAll} />}

      {/* Domain Authority & Competitor Comparison — Moz */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <SectionHeader
          icon={<FaChartLine size={14} />}
          title="Domain Authority & Competitor Comparison — Moz"
        />
        {mozLoading ? (
          <div className="flex justify-center py-6">
            <div className="w-6 h-6 border-4 border-violet-600 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : mozError ? (
          <ErrorBox message={mozError} onRetry={fetchAll} />
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-100">
                    <th className="text-left py-2 px-3 text-xs font-semibold text-gray-500">Domain</th>
                    <th className="text-center py-2 px-3 text-xs font-semibold text-gray-500">
                      DA <InfoTooltip text="Domain Authority (DA) is a score from 1–100 that predicts how well a website ranks on Google. Higher is better. It's based on the quality and quantity of links pointing to the site. Built by Moz." />
                    </th>
                    <th className="text-center py-2 px-3 text-xs font-semibold text-gray-500">
                      PA <InfoTooltip text="Page Authority (PA) works like DA but scores a single page rather than the whole domain. A high PA page ranks more easily in search results on its own." />
                    </th>
                    <th className="text-right py-2 px-3 text-xs font-semibold text-gray-500">
                      Linking Domains <InfoTooltip text="The number of unique websites that link back to this domain. These are called backlinks. More quality backlinks from trusted sites = higher Domain Authority." />
                    </th>
                    <th className="text-right py-2 px-3 text-xs font-semibold text-gray-500">
                      Spam Score <InfoTooltip text="Moz's estimate of how likely Google is to penalise this site for spammy behaviour. Under 10 is healthy, over 30 is a red flag. Lower is always better." />
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {mozSites.map(({ domain, domain_authority, page_authority, linking_domains, spam_score }) => {
                    const isOurs = domain === 'hiremeja.com';
                    return (
                      <tr
                        key={domain}
                        className={`border-b border-gray-50 ${isOurs ? 'bg-blue-50' : 'hover:bg-gray-50'}`}
                      >
                        <td className="py-3 px-3 font-medium text-gray-800">
                          <div className="flex items-center gap-2">
                            {isOurs && (
                              <span className="text-xs bg-blue-950 text-white px-1.5 py-0.5 rounded font-semibold">
                                You
                              </span>
                            )}
                            {domain}
                          </div>
                        </td>
                        <td className="py-3 px-3 text-center">
                          <span className={`text-lg font-bold ${isOurs ? 'text-violet-700' : 'text-gray-700'}`}>
                            {domain_authority ?? '—'}
                          </span>
                        </td>
                        <td className="py-3 px-3 text-center font-medium text-gray-600">
                          {page_authority ?? '—'}
                        </td>
                        <td className="py-3 px-3 text-right text-gray-600">
                          {fmt(linking_domains)}
                        </td>
                        <td className="py-3 px-3 text-right">
                          <span className={`font-medium ${
                            spam_score != null && spam_score > 30
                              ? 'text-red-500'
                              : 'text-green-600'
                          }`}>
                            {spam_score ?? '—'}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {moz?._cachedAt && (
              <p className="text-xs text-gray-400 mt-4">
                {moz._stale ? '⚠ Showing stale data · ' : 'Cached · '}
                Last fetched from Moz:{' '}
                {new Date(moz._cachedAt).toLocaleString('en-US', {
                  month: 'short', day: 'numeric',
                  hour: 'numeric', minute: '2-digit',
                })}
                {' '}· refreshes every 24 hours
              </p>
            )}
          </>
        )}
      </div>

      {/* GA4 sections */}
      {loading ? (
        <LoadingSpinner />
      ) : (
        <>
          {/* Stat cards */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
            <StatCard
              icon={<FaSearch size={18} />}
              label="Organic Traffic"
              value={organicSessions != null ? fmt(organicSessions) : '—'}
              sub={`Last ${days} days`}
              color="text-green-700"
              bgColor="bg-green-50"
              tooltip="Visitors who found your site through unpaid Google search results. This is the most valuable traffic channel — high organic traffic means your SEO is working."
            />
            <StatCard
              icon={<FaUsers size={18} />}
              label="Sessions"
              value={fmt(totalSessions)}
              sub={`Last ${days} days`}
              tooltip="A session is one visit to your site. A single person can create multiple sessions if they leave and return. This shows total visits, not unique visitors."
            />
            <StatCard
              icon={<FaUser size={18} />}
              label="Active Users"
              value={fmt(totalUsers)}
              sub={`${fmt(newUsers)} new users`}
              tooltip="Unique people who visited your site in this period. 'New users' are those visiting for the first time — a sign of how well you're reaching new audiences."
            />
            <StatCard
              icon={<FaEye size={18} />}
              label="Page Views"
              value={fmt(totalPageviews)}
              sub={`Last ${days} days`}
              tooltip="Total number of pages loaded across all visits. A single visitor can view many pages in one session. Higher page views often indicate strong content engagement."
            />
            <StatCard
              icon={<FaPercentage size={18} />}
              label="Engagement Rate"
              value={pct(engagementRate)}
              sub={`Avg. ${fmtDuration(avgDuration)} / session`}
              tooltip="The percentage of sessions where a visitor actively interacted — scrolled, clicked, or spent at least 10 seconds on the site. A higher rate means visitors find your content relevant."
            />
          </div>

          {/* Sessions & Users chart */}
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <SectionHeader icon={<FaUsers size={15} />} title="Sessions & Users Over Time" />
            {chartData.length === 0 ? (
              <p className="text-sm text-gray-400 text-center py-8">No data for this period.</p>
            ) : (
              <ResponsiveContainer width="100%" height={280}>
                <LineChart data={chartData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis
                    dataKey="date"
                    tickFormatter={formatGa4Date}
                    tick={{ fontSize: 11, fill: '#6b7280' }}
                    tickLine={false}
                  />
                  <YAxis yAxisId="sessions" tick={{ fontSize: 11, fill: '#6b7280' }} tickLine={false} axisLine={false} />
                  <YAxis yAxisId="users" orientation="right" tick={{ fontSize: 11, fill: '#6b7280' }} tickLine={false} axisLine={false} />
                  <Tooltip
                    labelFormatter={formatGa4Date}
                    contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e5e7eb' }}
                  />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Line yAxisId="sessions" type="monotone" dataKey="Sessions" stroke="#1e3a5f" strokeWidth={2} dot={false} />
                  <Line yAxisId="users"    type="monotone" dataKey="Users"    stroke="#cddd3a" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            )}
          </div>

          {/* Top Search Queries — GSC */}
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <SectionHeader
              icon={<FaSearch size={14} />}
              title="Top Search Queries — Google Search Console"
            />
            {gscLoading ? (
              <div className="flex justify-center py-6">
                <div className="w-6 h-6 border-4 border-blue-950 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : gscError ? (
              <ErrorBox message={gscError} />
            ) : !gscQueries?.length ? (
              <p className="text-sm text-gray-400 text-center py-6">No keyword data available.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-100">
                      <th className="text-left py-2 px-2 text-xs font-semibold text-gray-500 w-8">#</th>
                      <th className="text-left py-2 px-2 text-xs font-semibold text-gray-500">Keyword</th>
                      <th className="text-right py-2 px-2 text-xs font-semibold text-gray-500">Clicks</th>
                      <th className="text-right py-2 px-2 text-xs font-semibold text-gray-500">Impressions</th>
                      <th className="text-right py-2 px-2 text-xs font-semibold text-gray-500">CTR</th>
                      <th className="text-right py-2 px-2 text-xs font-semibold text-gray-500">Position</th>
                    </tr>
                  </thead>
                  <tbody>
                    {gscQueries.map((row, i) => (
                      <tr key={i} className="border-b border-gray-50 hover:bg-gray-50">
                        <td className="py-2 px-2 text-gray-400 text-xs">{i + 1}</td>
                        <td
                          className="py-2 px-2 text-gray-700 max-w-[280px] truncate"
                          title={row.keys?.[0]}
                        >
                          {row.keys?.[0]}
                        </td>
                        <td className="py-2 px-2 text-right font-medium text-blue-950">
                          {fmt(row.clicks)}
                        </td>
                        <td className="py-2 px-2 text-right text-gray-500">
                          {fmt(row.impressions)}
                        </td>
                        <td className="py-2 px-2 text-right text-gray-500">
                          {row.ctr != null ? `${(row.ctr * 100).toFixed(1)}%` : '—'}
                        </td>
                        <td className="py-2 px-2 text-right">
                          <PositionBadge pos={row.position} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Traffic Channels */}
            <div className="bg-white border border-gray-200 rounded-lg p-6">
              <SectionHeader icon={<FaFilter size={14} />} title="Traffic Sources" />
              {!channels?.length ? (
                <p className="text-sm text-gray-400 text-center py-6">No channel data available.</p>
              ) : (
                <div className="space-y-3">
                  {channels.map((r, i) => {
                    const channel = dim(r, 0);
                    const sessions = met(r, 0);
                    const maxSessions = met(channels[0], 0) || 1;
                    const barWidth = Math.round((sessions / maxSessions) * 100);
                    return (
                      <div key={i}>
                        <div className="flex justify-between text-sm mb-1">
                          <span className="text-gray-700">{channel || 'Unassigned'}</span>
                          <span className="font-medium text-blue-950">{fmt(sessions)} sessions</span>
                        </div>
                        <div className="h-1.5 bg-gray-100 rounded-full">
                          <div
                            className="h-1.5 rounded-full"
                            style={{ width: `${barWidth}%`, backgroundColor: channelColor(channel) }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Top Pages */}
            <div className="bg-white border border-gray-200 rounded-lg p-6">
              <SectionHeader icon={<FaFileAlt size={14} />} title="Top Pages" />
              {!pages?.length ? (
                <p className="text-sm text-gray-400 text-center py-6">No page data available.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-100">
                        <th className="text-left py-2 px-1 text-xs font-semibold text-gray-500">Page</th>
                        <th className="text-right py-2 px-1 text-xs font-semibold text-gray-500">Views</th>
                        <th className="text-right py-2 px-1 text-xs font-semibold text-gray-500">Users</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pages.map((r, i) => (
                        <tr key={i} className="border-b border-gray-50 hover:bg-gray-50">
                          <td
                            className="py-2 px-1 text-gray-700 max-w-[200px] truncate"
                            title={dim(r, 0)}
                          >
                            {dim(r, 0) || '/'}
                          </td>
                          <td className="py-2 px-1 text-right font-medium text-blue-950">{fmt(met(r, 0))}</td>
                          <td className="py-2 px-1 text-right text-gray-500">{fmt(met(r, 1))}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Countries */}
            <div className="bg-white border border-gray-200 rounded-lg p-6">
              <SectionHeader icon={<FaGlobe size={14} />} title="Top Countries" />
              {!countries?.length ? (
                <p className="text-sm text-gray-400 text-center py-6">No country data available.</p>
              ) : (
                <div className="space-y-2">
                  {countries.map((r, i) => {
                    const maxSessions = met(countries[0], 0) || 1;
                    const sessions = met(r, 0);
                    const barWidth = Math.round((sessions / maxSessions) * 100);
                    return (
                      <div key={i}>
                        <div className="flex justify-between text-sm mb-1">
                          <span className="text-gray-700">{dim(r, 0)}</span>
                          <span className="font-medium text-blue-950">{fmt(sessions)} sessions</span>
                        </div>
                        <div className="h-1.5 bg-gray-100 rounded-full">
                          <div className="h-1.5 bg-blue-950 rounded-full" style={{ width: `${barWidth}%` }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Devices */}
            <div className="bg-white border border-gray-200 rounded-lg p-6">
              <SectionHeader icon={<FaMobileAlt size={14} />} title="Device Breakdown" />
              {!devices?.length ? (
                <p className="text-sm text-gray-400 text-center py-6">No device data available.</p>
              ) : (
                <div className="space-y-4">
                  {devices.map((r, i) => {
                    const totalDeviceSessions = devices.reduce(
                      (s, d) => s + met(d, 0), 0
                    );
                    const sessions = met(r, 0);
                    const share = totalDeviceSessions > 0
                      ? ((sessions / totalDeviceSessions) * 100).toFixed(1)
                      : 0;
                    const colors = ['bg-blue-950', 'bg-[#cddd3a]', 'bg-blue-400'];
                    return (
                      <div key={i}>
                        <div className="flex justify-between text-sm mb-1">
                          <span className="text-gray-700 capitalize">{dim(r, 0)}</span>
                          <span className="font-medium text-blue-950">{share}% · {fmt(sessions)} sessions</span>
                        </div>
                        <div className="h-2 bg-gray-100 rounded-full">
                          <div
                            className={`h-2 rounded-full ${colors[i] || 'bg-gray-400'}`}
                            style={{ width: `${share}%` }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default MarketingDashboard;
